/**
 * Small shadcn-style primitives, co-located: Badge, Input, Textarea, Kbd,
 * Skeleton, Separator, ScrollArea, Progress, Checkbox.
 */
import { Checkbox as BaseCheckbox } from "@base-ui-components/react/checkbox";
import { Progress as BaseProgress } from "@base-ui-components/react/progress";
import { ScrollArea as BaseScrollArea } from "@base-ui-components/react/scroll-area";
import { Check } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

// One Separator implementation (`./separator`, Base UI + `--border` token),
// re-exported here so both import paths resolve to the SAME component. The
// former obsidian-only `--gv-line` wrapper collapsed onto `--border` in the
// Wave 2b token sweep, leaving nothing to specialize. */
export { Separator } from "./separator";

export function Badge({
  className,
  color,
  children,
}: {
  className?: string;
  color?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-px font-mono text-2xs uppercase tracking-wider text-muted-foreground",
        className,
      )}
    >
      {color && (
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      )}
      {children}
    </span>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "h-[34px] w-full rounded-md border border-border bg-input px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      "w-full rounded-md border border-border bg-input px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none",
      className,
    )}
    {...props}
  />
));
Textarea.displayName = "Textarea";

export function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-border bg-muted px-1 font-mono text-[10px] text-muted-foreground">
      {children}
    </kbd>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton", className)} />;
}

export function ScrollArea({
  className,
  children,
  viewportRef,
}: {
  className?: string;
  children: React.ReactNode;
  viewportRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <BaseScrollArea.Root className={cn("overflow-hidden", className)}>
      <BaseScrollArea.Viewport ref={viewportRef} className="h-full w-full [&>div]:!block">
        {children}
      </BaseScrollArea.Viewport>
      <BaseScrollArea.Scrollbar
        orientation="vertical"
        className="flex w-2 touch-none select-none p-px"
      >
        <BaseScrollArea.Thumb className="relative flex-1 rounded-full bg-border hover:bg-muted-foreground/40" />
      </BaseScrollArea.Scrollbar>
    </BaseScrollArea.Root>
  );
}

export function Progress({ value, className }: { value: number; className?: string }) {
  return (
    <BaseProgress.Root
      value={value}
      className={cn("relative h-1 w-full overflow-hidden rounded-full bg-muted", className)}
    >
      {/* Base UI computes the fill width itself (inline `width: n%` on the
          indicator) — no manual transform math needed here. */}
      <BaseProgress.Indicator className="block h-full bg-primary transition-[width] duration-500" />
    </BaseProgress.Root>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
      <BaseCheckbox.Root
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v)}
        className="flex h-4 w-4 items-center justify-center rounded border border-border bg-input data-[checked]:border-primary data-[checked]:bg-primary"
      >
        <BaseCheckbox.Indicator>
          <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
      {label}
    </label>
  );
}
